import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import {
  ArrowLeft, Circle, Square, Shield, Swords, ZoomIn, ZoomOut,
  Camera, Loader2, CheckCircle2, Radio, Goal, Flag,
} from "lucide-react";
import { toast } from "sonner";

const QUARTERS = ["1", "2", "3", "4"];

function pickMime() {
  const cands = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  for (const c of cands) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

export default function RecorderStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef(null);
  const trackRef = useRef(null);

  const [game, setGame] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [ready, setReady] = useState(false);
  const [camError, setCamError] = useState("");

  const [unit, setUnit] = useState("offense");
  const [quarter, setQuarter] = useState("1");
  const [view, setView] = useState("sideline");
  const [recording, setRecording] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [clipCount, setClipCount] = useState(0);
  const [flash, setFlash] = useState(false);

  // zoom
  const [zoomCaps, setZoomCaps] = useState(null); // {min,max,step} hardware
  const [zoom, setZoom] = useState(1);

  const loadGame = useCallback(async () => {
    try {
      const { data } = await api.get(`/games/${gameId}`);
      setGame(data);
      setClipCount(data.clip_count || 0);
    } catch {
      toast.error("Game not found");
      navigate("/");
    }
  }, [gameId, navigate]);

  const startCamera = useCallback(async (id) => {
    setCamError("");
    setReady(false);
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const constraints = {
        video: id ? { deviceId: { exact: id } } : { facingMode: "environment" },
        audio: true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      const track = stream.getVideoTracks()[0];
      trackRef.current = track;
      // hardware zoom capability detection
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      if (caps && caps.zoom) {
        setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
        setZoom(caps.zoom.min || 1);
      } else {
        setZoomCaps(null);
        setZoom(1);
      }
      // enumerate devices (labels available after permission)
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === "videoinput"));
      if (!id) {
        const cur = track.getSettings ? track.getSettings().deviceId : "";
        if (cur) setDeviceId(cur);
      }
      setReady(true);
    } catch (e) {
      setCamError(e.name === "NotAllowedError"
        ? "Camera access denied. Enable camera permissions and reload."
        : "Unable to access camera. Connect a camera and reload.");
    }
  }, []);

  useEffect(() => {
    loadGame();
    startCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // apply zoom
  useEffect(() => {
    if (zoomCaps && trackRef.current && trackRef.current.applyConstraints) {
      trackRef.current.applyConstraints({ advanced: [{ zoom }] }).catch(() => {});
    }
    if (!zoomCaps && videoRef.current) {
      videoRef.current.style.transform = `scale(${zoom})`;
    }
  }, [zoom, zoomCaps]);

  const changeDevice = (e) => {
    const id = e.target.value;
    setDeviceId(id);
    startCamera(id);
  };

  const zoomRange = zoomCaps || { min: 1, max: 4, step: 0.1 };
  const bumpZoom = (dir) => {
    setZoom((z) => {
      const next = Math.min(zoomRange.max, Math.max(zoomRange.min, +(z + dir * (zoomRange.step * 4 || 0.4)).toFixed(2)));
      return next;
    });
  };

  const uploadClip = async (blob, dur, capturedUnit, capturedQuarter, capturedView) => {
    setUploadingCount((n) => n + 1);
    const fd = new FormData();
    fd.append("file", blob, "clip.webm");
    fd.append("game_id", gameId);
    fd.append("unit", capturedUnit);
    fd.append("quarter", capturedQuarter);
    fd.append("view", capturedView);
    fd.append("duration", String(dur));
    try {
      const { data } = await api.post("/clips", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setClipCount((c) => Math.max(c, data.clip_number));
      setFlash(true);
      setTimeout(() => setFlash(false), 500);
      toast.success(`Clip #${data.clip_number} uploaded`, {
        description: `${capturedView === "endzone" ? "Endzone" : "Sideline"} · ${capturedUnit === "offense" ? "Offense" : "Defense"} · Q${capturedQuarter}`,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingCount((n) => Math.max(0, n - 1));
    }
  };

  const startRecording = () => {
    if (!streamRef.current || recording) return;
    chunksRef.current = [];
    const mime = pickMime();
    const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    recorderRef.current = rec;
    const capturedUnit = unit;
    const capturedQuarter = quarter;
    const capturedView = view;
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });
      const dur = (Date.now() - startTimeRef.current) / 1000;
      // fire-and-forget upload so the recorder can immediately start the next clip
      uploadClip(blob, dur, unitAtStop.current, quarterAtStop.current, viewAtStop.current);
    };
    rec.start();
    startTimeRef.current = Date.now();
    unitAtStop.current = capturedUnit;
    quarterAtStop.current = capturedQuarter;
    viewAtStop.current = capturedView;
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((Date.now() - startTimeRef.current) / 1000);
    }, 100);
    toast("Recording started", { icon: <Radio className="h-4 w-4 text-primary" /> });
  };

  // refs to hold latest tag values for onstop closure
  const unitAtStop = useRef(unit);
  const quarterAtStop = useRef(quarter);
  const viewAtStop = useRef(view);
  useEffect(() => { unitAtStop.current = unit; }, [unit]);
  useEffect(() => { quarterAtStop.current = quarter; }, [quarter]);
  useEffect(() => { viewAtStop.current = view; }, [view]);

  const stopRecording = () => {
    if (!recording) return;
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    recorderRef.current && recorderRef.current.stop();
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const zoomPct = zoomCaps
    ? (((zoom - zoomRange.min) / (zoomRange.max - zoomRange.min)) * (zoomRange.max / zoomRange.min - 1) + 1)
    : zoom;

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* top bar */}
      <div className="h-14 px-4 flex items-center justify-between border-b border-border bg-[#0d0d0d] shrink-0">
        <button onClick={() => navigate("/")} data-testid="record-back-button"
          className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground hover:text-white transition-colors">
          <ArrowLeft className="h-5 w-5" strokeWidth={2.5} /> Exit
        </button>
        <div className="text-center">
          <div className="font-heading text-lg font-bold uppercase tracking-tight leading-none">{game?.name || "Recording"}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{game?.opponent ? `vs ${game.opponent}` : "Live capture"}</div>
        </div>
        <div className="flex items-center gap-1.5 font-heading text-lg font-black tabular-nums">
          <span className="text-muted-foreground text-xs uppercase tracking-widest font-bold">Clips</span>
          <span data-testid="clip-count" className="text-white">{clipCount}</span>
        </div>
      </div>

      {/* camera viewport */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden bg-black">
        <div className={`relative w-full max-w-5xl aspect-video bg-black overflow-hidden ${recording ? "rec-frame" : ""}`}>
          <video ref={videoRef} data-testid="camera-preview" playsInline muted
            className="absolute inset-0 h-full w-full object-cover origin-center transition-transform duration-150" />
          {flash && <div className="absolute inset-0 bg-white flash-save pointer-events-none" />}

          {/* HUD top-left rec + timer */}
          {recording && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 px-3 py-1.5 rounded-md">
              <Circle className="h-3 w-3 fill-primary text-primary rec-dot" />
              <span className="font-heading text-xl font-black tabular-nums text-white">{fmt(elapsed)}</span>
            </div>
          )}

          {/* HUD top-right current tag */}
          <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
            <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase tracking-widest ${unit === "offense" ? "bg-primary text-white" : "bg-secondary text-black"}`}>
              {unit}
            </span>
            <span className="px-2.5 py-1 rounded bg-black/70 text-xs font-bold uppercase tracking-widest text-white">Q{quarter}</span>
            <span className="px-2.5 py-1 rounded bg-black/70 text-xs font-bold uppercase tracking-widest text-white flex items-center gap-1">
              {view === "endzone" ? <Goal className="h-3 w-3" strokeWidth={2.75} /> : <Flag className="h-3 w-3" strokeWidth={2.75} />}
              {view}
            </span>
          </div>

          {/* zoom indicator */}
          <div className="absolute bottom-4 left-4 bg-black/70 px-3 py-1.5 rounded-md">
            <span className="font-heading text-sm font-bold tabular-nums text-white">{zoomPct.toFixed(1)}×</span>
          </div>

          {uploadingCount > 0 && (
            <div data-testid="uploading-indicator" className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/70 px-3 py-1.5 rounded-md">
              <Loader2 className="h-4 w-4 animate-spin text-secondary" />
              <span className="text-xs font-bold uppercase tracking-widest text-white">Uploading {uploadingCount}</span>
            </div>
          )}

          {!ready && !camError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground uppercase tracking-widest font-bold">Starting camera…</p>
            </div>
          )}
          {camError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black p-8 text-center">
              <Camera className="h-12 w-12 text-primary mb-4" strokeWidth={1.5} />
              <p data-testid="camera-error" className="text-white font-semibold max-w-sm">{camError}</p>
              <Button onClick={() => startCamera(deviceId)} className="mt-5 bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-widest">Retry</Button>
            </div>
          )}
        </div>
      </div>

      {/* controls */}
      <div className="shrink-0 bg-[#0d0d0d] border-t border-border p-4 sm:p-5">
        <div className="max-w-5xl mx-auto space-y-4">
          {/* tag buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="tag-offense" onClick={() => setUnit("offense")}
              className={`h-16 sm:h-20 rounded-md border-2 flex items-center justify-center gap-2.5 font-heading text-xl sm:text-2xl font-black uppercase tracking-wide transition-all duration-200 active:scale-[0.98] ${
                unit === "offense" ? "bg-primary border-primary text-white shadow-[0_0_24px_rgba(255,59,48,0.4)]" : "bg-card border-border text-muted-foreground hover:border-primary/50"}`}>
              <Swords className="h-6 w-6" strokeWidth={2.75} /> Offense
            </button>
            <button data-testid="tag-defense" onClick={() => setUnit("defense")}
              className={`h-16 sm:h-20 rounded-md border-2 flex items-center justify-center gap-2.5 font-heading text-xl sm:text-2xl font-black uppercase tracking-wide transition-all duration-200 active:scale-[0.98] ${
                unit === "defense" ? "bg-secondary border-secondary text-black shadow-[0_0_24px_rgba(0,229,255,0.4)]" : "bg-card border-border text-muted-foreground hover:border-secondary/50"}`}>
              <Shield className="h-6 w-6" strokeWidth={2.75} /> Defense
            </button>
          </div>

          {/* angle selector */}
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="view-sideline" onClick={() => setView("sideline")}
              className={`h-14 rounded-md border-2 flex items-center justify-center gap-2 font-heading text-lg font-black uppercase tracking-wide transition-all duration-200 active:scale-[0.98] ${
                view === "sideline" ? "bg-white border-white text-black" : "bg-card border-border text-muted-foreground hover:border-white/40"}`}>
              <Flag className="h-5 w-5" strokeWidth={2.75} /> Sideline
            </button>
            <button data-testid="view-endzone" onClick={() => setView("endzone")}
              className={`h-14 rounded-md border-2 flex items-center justify-center gap-2 font-heading text-lg font-black uppercase tracking-wide transition-all duration-200 active:scale-[0.98] ${
                view === "endzone" ? "bg-white border-white text-black" : "bg-card border-border text-muted-foreground hover:border-white/40"}`}>
              <Goal className="h-5 w-5" strokeWidth={2.75} /> Endzone
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            {/* quarter */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mr-1">Qtr</span>
              {QUARTERS.map((q) => (
                <button key={q} data-testid={`quarter-${q}`} onClick={() => setQuarter(q)}
                  className={`h-11 w-11 rounded-md border-2 font-heading text-lg font-black transition-all duration-200 active:scale-95 ${
                    quarter === q ? "bg-white border-white text-black" : "bg-card border-border text-muted-foreground hover:border-white/40"}`}>
                  {q}
                </button>
              ))}
            </div>

            {/* zoom */}
            <div className="flex items-center gap-3 flex-1">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Zoom</span>
              <Button variant="outline" size="icon" data-testid="zoom-out" onClick={() => bumpZoom(-1)}
                className="h-10 w-10 border-border bg-card hover:bg-accent shrink-0">
                <ZoomOut className="h-5 w-5" strokeWidth={2.5} />
              </Button>
              <Slider data-testid="zoom-slider" value={[zoom]} min={zoomRange.min} max={zoomRange.max} step={zoomRange.step}
                onValueChange={(v) => setZoom(v[0])} className="flex-1" />
              <Button variant="outline" size="icon" data-testid="zoom-in" onClick={() => bumpZoom(1)}
                className="h-10 w-10 border-border bg-card hover:bg-accent shrink-0">
                <ZoomIn className="h-5 w-5" strokeWidth={2.5} />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* device selector */}
            {devices.length > 1 && (
              <select data-testid="camera-select" value={deviceId} onChange={changeDevice} disabled={recording}
                className="h-14 rounded-md bg-card border border-border px-3 text-sm font-medium text-white max-w-[40%] disabled:opacity-50">
                {devices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                ))}
              </select>
            )}

            {/* record / stop */}
            {!recording ? (
              <button data-testid="record-start-button" onClick={startRecording} disabled={!ready}
                className="flex-1 h-14 rounded-md bg-primary hover:bg-red-500 disabled:opacity-40 text-white font-heading text-xl font-black uppercase tracking-widest flex items-center justify-center gap-2.5 transition-colors active:scale-[0.99]">
                <Circle className="h-6 w-6 fill-white" /> Record
              </button>
            ) : (
              <button data-testid="record-stop-button" onClick={stopRecording}
                className="flex-1 h-14 rounded-md bg-white hover:bg-gray-200 text-black font-heading text-xl font-black uppercase tracking-widest flex items-center justify-center gap-2.5 transition-colors active:scale-[0.99]">
                <Square className="h-6 w-6 fill-black" /> Stop & Upload
              </button>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" /> Uploads run in the background — hit record again right away</p>
        </div>
      </div>
    </div>
  );
}
